import { createBrowserRouter, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { useExperiencePreset } from '@/hooks/useFeature';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Providers } from '@/components/providers';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { FirmManagedRouteGuard } from '@/components/FirmManagedRouteGuard';
import { FeatureRouteGuard } from '@/components/features/FeatureRouteGuard';
import { AdminProtectedRoute } from '@/components/AdminProtectedRoute';
import { AppLayout } from '@/components/layout';
import { AdvisorProvider } from '@/contexts/AdvisorContext';
import HubPublicWidgets from '@/components/integrations/HubPublicWidgets';
import { HelmetProvider } from 'react-helmet-async';
import {
  RouteErrorBoundary,
  NestedRouteErrorBoundary,
  NotFoundPage,
} from '@/components/RouteErrorBoundary';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from '@/lib/i18n';
import { I18nLoader } from '@/components/i18n';
import { lazyWithRetry } from '@/lib/errorHandling/lazyWithRetry';
import { AdminCommandPalette } from '@/components/admin/AdminCommandPalette';

// Lazy load all pages for code splitting
// Critical pages use lazyWithRetry for better resilience
// App pages (protected, requires onboarding)
const DashboardPage = lazyWithRetry(() => import('@/pages/app/dashboard/page'));
const IntakePage = lazyWithRetry(() => import('@/pages/app/intake/page'));
const NetWorthPage = lazyWithRetry(() => import('@/pages/app/net-worth/page'));
const ChildrenPage = lazyWithRetry(() => import('@/pages/app/children/page'));
const SupportCalculatorPage = lazyWithRetry(() => import('@/pages/app/support-calculator/page'));
const AssetEmotionsPage = lazyWithRetry(() => import('@/pages/app/asset-emotions/page'));
const GoalsPage = lazyWithRetry(() => import('@/pages/app/goals/page'));
const StrategyPage = lazyWithRetry(() => import('@/pages/app/strategy/page'));
const CaseIntelligencePage = lazyWithRetry(() => import('@/pages/app/case-intelligence/page'));
const KnowYourSpousePage = lazyWithRetry(() => import('@/pages/app/know-your-spouse/page'));
const SessionDebriefPage = lazyWithRetry(() => import('@/pages/app/session-debrief/page'));
const DocumentsPage = lazyWithRetry(() => import('@/pages/app/documents/page'));
const TasksPage = lazyWithRetry(() => import('@/pages/app/tasks/page'));
const DocumentDetailPage = lazyWithRetry(() => import('@/pages/app/documents/[id]/page'));
const EntityPlanningPage = lazyWithRetry(() => import('@/pages/app/entity-planning/page'));
const ExportPage = lazyWithRetry(() => import('@/pages/app/export/page'));
const NotesPage = lazyWithRetry(() => import('@/pages/app/notes/page'));
const MyTeamPage = lazyWithRetry(() => import('@/pages/app/my-team/page'));
const LegalTeamPage = lazyWithRetry(() => import('@/pages/app/legal-team/page'));
const SettingsPage = lazyWithRetry(() => import('@/pages/app/settings/page'));
const LearnPage = lazyWithRetry(() => import('@/pages/app/learn/page'));
const PathFinderPage = lazyWithRetry(() => import('@/pages/app/learn/path-finder/page'));
const LearnCategoryPage = lazyWithRetry(() => import('@/pages/app/learn/[category]/page'));
const LearnArticlePage = lazyWithRetry(() => import('@/pages/app/learn/[category]/[slug]/page'));
const OnboardingPage = lazyWithRetry(() => import('@/pages/app/onboarding/page'));
const CollectorOnboardingPage = lazyWithRetry(() => import('@/pages/app/onboarding/collector/CollectorOnboarding'));
const WelcomePaidPage = lazyWithRetry(() => import('@/pages/app/welcome-paid/page'));
const UpgradePage = lazyWithRetry(() => import('@/pages/app/upgrade/page'));
const AskSagePage = lazyWithRetry(() => import('@/pages/app/ask-sage/page'));
const FinancialDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/page'));
const FinancialDisclosureConnectPage = lazyWithRetry(() => import('@/pages/app/financials/connect/page'));
const BusinessDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/business/page'));
const RealEstateDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/real-estate/page'));
const VehiclesDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/vehicles/page'));
const BankAccountsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/bank-accounts/page'));
const CreditCardsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/credit-cards/page'));
const InvestmentsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/investments/page'));
const RetirementDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/retirement/page'));
const LifeInsuranceDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/life-insurance/page'));
const HealthInsuranceDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/health-insurance/page'));
const PCInsuranceDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/pc-insurance/page'));
const TaxReturnsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/tax-returns/page'));
const ThirdPartyPaymentsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/third-party-payments/page'));
const CryptoDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/crypto/page'));
const PayStubsDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/pay-stubs/page'));
const SSAStatementDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/ssa-statement/page'));
const PromissoryNotesDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/promissory-notes/page'));
const LeasesDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/leases/page'));
const CreditReportDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/credit-report/page'));
const MortgagesDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/mortgages/page'));
const ContingentLiabilitiesDisclosurePage = lazyWithRetry(() => import('@/pages/app/financials/contingent-liabilities/page'));
const CorrespondencePage = lazyWithRetry(() => import('@/pages/app/correspondence/page'));
const CorrespondenceDetailPage = lazyWithRetry(() => import('@/pages/app/correspondence/[id]/page'));
const HelpPage = lazyWithRetry(() => import('@/pages/app/help/page'));
const GuidePage = lazyWithRetry(() => import('@/pages/app/help/guide'));
const LegalProcessPage = lazyWithRetry(() => import('@/pages/app/legal-process/page'));
const SettlementWizardPage = lazyWithRetry(() => import('@/pages/app/settlement-wizard/page'));
const SettlementSummaryPage = lazyWithRetry(() => import('@/pages/app/settlement-wizard/summary/page'));
const RedlinesPage = lazyWithRetry(() => import('@/pages/app/settlement-wizard/redlines/page'));
const SibylPage = lazyWithRetry(() => import('@/pages/app/sibyl/page'));
const ForensicReportPage = lazyWithRetry(() => import('@/pages/app/forensic-report/page'));
const DiscoveryInboxPage = lazyWithRetry(() => import('@/pages/app/discovery-inbox/page'));
const RedlineListPage = lazyWithRetry(() => import('@/pages/app/redline/page'));
const RedlineWorkspacePage = lazyWithRetry(() => import('@/pages/app/redline/[id]/page'));
const CaseLibraryPage = lazyWithRetry(() => import('@/pages/app/case-library/page'));
const DiscoveryPage = lazyWithRetry(() => import('@/pages/app/discovery/page'));
const CaseReadinessPage = lazyWithRetry(() => import('@/pages/app/case-readiness/page'));
const CollectionPage = lazyWithRetry(() => import('@/pages/app/collection/page'));
const OAuthAuthorizePage = lazyWithRetry(() => import('@/pages/app/oauth-authorize/page'));

// Advisor pages (protected, no onboarding required)
const AdvisorDashboardPage = lazyWithRetry(() => import('@/pages/advisor/advisor/page'));
const AdvisorAcceptPage = lazyWithRetry(() => import('@/pages/advisor/advisor/accept/page'));
const AdvisorAddClientPage = lazyWithRetry(() => import('@/pages/advisor/advisor/add-client/page'));
const AdvisorCasePage = lazyWithRetry(() => import('@/pages/advisor/advisor/case/[clientId]/page'));
const AdvisorBillingPage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/billing/page'));
const AdvisorPrivilegePage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/privilege/page'));
// /privilege (#1877): the public page for counsel — what the platform does to place a client's work under
// their lawyer's direction, what it records, and what it does not decide.
const PrivilegePage = lazyWithRetry(() => import('@/pages/marketing/privilege/page'));
const AdvisorCompliancePage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/compliance/page'));
const AdvisorTeamPage = lazyWithRetry(() => import('@/pages/advisor/advisor/team/page'));
const AdvisorTeamAcceptPage = lazyWithRetry(() => import('@/pages/advisor/advisor/team/accept/page'));
const AdvisorAskSagePage = lazyWithRetry(() => import('@/pages/advisor/advisor/ask-sage/page'));
const AdvisorReferralsPage = lazyWithRetry(() => import('@/pages/advisor/advisor/referrals/page'));
const AdvisorBrandingPage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/branding/page'));
const AdvisorFeaturesPage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/features/page'));
const AdvisorIntegrationsPage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/integrations/page'));
const AdvisorLaunchChecklistPage = lazyWithRetry(() => import('@/pages/advisor/advisor/settings/launch-checklist/page'));

// Admin pages (protected, requires admin role)
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/dashboard/page'));
const AdminProfitabilityPage = lazyWithRetry(() => import('@/pages/admin/profitability/page'));
const AdminLegalProcessPage = lazyWithRetry(() => import('@/pages/admin/legal-process/page'));
const AdminClaimsPage = lazyWithRetry(() => import('@/pages/admin/claims/page'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/users/page'));
const AdminUserDetailPage = lazyWithRetry(() => import('@/pages/admin/users/[userId]/page'));
const AdminBannedEmailsPage = lazyWithRetry(() => import('@/pages/admin/banned-emails/page'));
const AdminGrowthIntelligencePage = lazyWithRetry(() => import('@/pages/admin/growth-intelligence/page'));
const AdminConversionIntelligencePage = lazyWithRetry(() => import('@/pages/admin/conversion-intelligence/page'));
const AdminPartnersPage = lazyWithRetry(() => import('@/pages/admin/partners/page'));
const AdminPartnerDetailPage = lazyWithRetry(() => import('@/pages/admin/partners/[advisorId]/page'));
const AdminProspectsPage = lazyWithRetry(() => import('@/pages/admin/prospects/page'));
const AdminProspectDetailPage = lazyWithRetry(() => import('@/pages/admin/prospects/[prospectId]/page'));
const AdminProspectImportPage = lazyWithRetry(() => import('@/pages/admin/prospects/import/page'));
const AdminLeaderboardPage = lazyWithRetry(() => import('@/pages/admin/partners/leaderboard/page'));
const AdminPortalsPage = lazyWithRetry(() => import('@/pages/admin/portals/page'));
const AdminCommissionsPage = lazyWithRetry(() => import('@/pages/admin/commissions/page'));
const AdminCohortAnalysisPage = lazyWithRetry(() => import('@/pages/admin/analytics/cohorts/page'));
const AdminReportsPage = lazyWithRetry(() => import('@/pages/admin/reports/page'));
const AdminOptOutsPage = lazyWithRetry(() => import('@/pages/admin/opt-outs/page'));
const AdminCampaignsPage = lazyWithRetry(() => import('@/pages/admin/campaigns/page'));
const AdminCampaignBuilderPage = lazyWithRetry(() => import('@/pages/admin/campaigns/builder/page'));
const AdminCampaignDetailPage = lazyWithRetry(() => import('@/pages/admin/campaigns/detail/page'));
const AdminCampaignTemplatesPage = lazyWithRetry(() => import('@/pages/admin/campaigns/templates/page'));
const AdminDedupPage = lazyWithRetry(() => import('@/pages/admin/prospects/dedup/page'));
const AdminPortalHealthPage = lazyWithRetry(() => import('@/pages/admin/portals/health/page'));
const AdminDemoRequestsPage = lazyWithRetry(() => import('@/pages/admin/demo-requests/page'));

// Auth pages (public) - use lazyWithRetry for critical auth flow
const AuthPage = lazyWithRetry(() => import('@/pages/auth/auth/page'));
const AuthCallbackPage = lazyWithRetry(() => import('@/pages/auth/auth/callback/page'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/auth/forgot-password/page'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/auth/reset-password/page'));
const AdvisorAuthPage = lazyWithRetry(() => import('@/pages/auth/advisor-auth/page'));
const AdvisorSignupPage = lazyWithRetry(() => import('@/pages/auth/advisor-signup/page'));
const ClientAcceptPage = lazyWithRetry(() => import('@/pages/auth/client-accept/page'));
const EmailConfirmedPage = lazyWithRetry(() => import('@/pages/auth/email-confirmed/page'));
const BetaInvitePage = lazyWithRetry(() => import('@/pages/auth/beta-invite/page'));
const ShareFileCallbackPage = lazyWithRetry(() => import('@/pages/auth/sharefile-callback/page'));
const GoogleDriveCallbackPage = lazyWithRetry(() => import('@/pages/auth/gdrive-callback/page'));
const SharePage = lazyWithRetry(() => import('@/pages/share/page'));

// Tenant login page (for white-label partner domains)
const TenantLoginPage = lazyWithRetry(() => import('@/pages/tenant/TenantLoginPage'));

// Marketing pages (public) — use lazyWithRetry to survive deploy chunk hash changes
const HomePage = lazyWithRetry(() => import('@/pages/HomePage'));
const AdvisorPricingPage = lazyWithRetry(() => import('@/pages/marketing/advisor-pricing/page'));
const AffordablePage = lazyWithRetry(() => import('@/pages/marketing/affordable/page'));
const AttorneysPage = lazyWithRetry(() => import('@/pages/marketing/attorneys/page'));
const BlogPage = lazyWithRetry(() => import('@/pages/marketing/blog/page'));
const BlogPostPage = lazyWithRetry(() => import('@/pages/marketing/blog/[slug]/page'));
const BusinessDivorcePage = lazyWithRetry(() => import('@/pages/marketing/business-divorce/page'));
const CalculatorPage = lazyWithRetry(() => import('@/pages/marketing/calculator/page'));
const CaseManagementPage = lazyWithRetry(() => import('@/pages/marketing/case-management/page'));
const CdfaPage = lazyWithRetry(() => import('@/pages/marketing/cdfa/page'));
const ComparePage = lazyWithRetry(() => import('@/pages/marketing/compare/page'));
const CpaPage = lazyWithRetry(() => import('@/pages/marketing/cpa/page'));
const DemoPage = lazyWithRetry(() => import('@/pages/marketing/demo/page'));
const DivorceStrategyPage = lazyWithRetry(() => import('@/pages/marketing/divorce-strategy/page'));
const DiyDivorcePage = lazyWithRetry(() => import('@/pages/marketing/diy-divorce/page'));
const FinancialAdvisorsPage = lazyWithRetry(() => import('@/pages/marketing/financial-advisors/page'));
const ForProfessionalsPage = lazyWithRetry(() => import('@/pages/marketing/for-professionals/page'));
const HighAssetPage = lazyWithRetry(() => import('@/pages/marketing/high-asset/page'));
const LawFirmsPage = lazyWithRetry(() => import('@/pages/marketing/law-firms/page'));
const MediatorsPage = lazyWithRetry(() => import('@/pages/marketing/mediators/page'));
const NegotiationPage = lazyWithRetry(() => import('@/pages/marketing/negotiation/page'));
const PricingPage = lazyWithRetry(() => import('@/pages/marketing/pricing/page'));
const PrivacyPage = lazyWithRetry(() => import('@/pages/marketing/privacy/page'));
const TrustPage = lazyWithRetry(() => import('@/pages/marketing/trust/page'));
const ProSePage = lazyWithRetry(() => import('@/pages/marketing/pro-se/page'));
const TermsPage = lazyWithRetry(() => import('@/pages/marketing/terms/page'));
const VideosPage = lazyWithRetry(() => import('@/pages/marketing/videos/page'));
const DivorcePreparationChecklistPage = lazyWithRetry(() => import('@/pages/marketing/guide/divorce-preparation-checklist/page'));
const PartnerLandingPage = lazyWithRetry(() => import('@/pages/marketing/partner/[slug]/page'));
const PartnerSignupPage = lazyWithRetry(() => import('@/pages/marketing/partner/signup/page'));
const PartnerTermsPage = lazyWithRetry(() => import('@/pages/marketing/partner/terms/page'));
const VsShareFilePage = lazyWithRetry(() => import('@/pages/marketing/vs-sharefile/page'));
const WhiteLabelPage = lazyWithRetry(() => import('@/pages/marketing/white-label/page'));
const PilotSignupPage = lazyWithRetry(() => import('@/pages/marketing/pilot/page'));
const UnsubscribePage = lazyWithRetry(() => import('@/pages/marketing/unsubscribe/page'));
const GuidesIndexPage = lazyWithRetry(() => import('@/pages/marketing/guides/page'));
const GuidesAdvisorPage = lazyWithRetry(() => import('@/pages/marketing/guides/advisor-guide'));
const GuidesClientPage = lazyWithRetry(() => import('@/pages/marketing/guides/client-guide'));

// Check if running on a white-label tenant domain (synchronous, no hooks needed)
function isTenantDomain(): boolean {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !!(window as any).__PARTNER_CONFIG__;
}

// Loading fallback
function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-dvh">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-600"></div>
    </div>
  );
}

// Locale synchronization component
// Syncs the URL locale parameter with i18next language setting
function LocaleSync() {
  const { locale } = useParams<{ locale?: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    const targetLang = locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale)
      ? locale
      : DEFAULT_LOCALE;

    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [locale, i18n]);

  return <Outlet />;
}

// Suspense wrapper for lazy loaded pages
function SuspensePage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

// Root layout with providers
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RootLayout() {
  return (
    <HelmetProvider>
      <I18nLoader>
        <Providers>
          <ScrollToTop />
          <HubPublicWidgets />
          <Outlet />
        </Providers>
      </I18nLoader>
    </HelmetProvider>
  );
}

// Protected app layout (requires onboarding and MFA)
function AppLayoutWrapper() {
  return (
    <ProtectedRoute requireOnboarding>
      <AppLayout>
        <FeatureRouteGuard>
          <SuspensePage>
            <Outlet />
          </SuspensePage>
        </FeatureRouteGuard>
      </AppLayout>
    </ProtectedRoute>
  );
}

// Protected app layout WITHOUT MFA requirement (for onboarding and settings)
// New users need to access these before setting up MFA
function AppLayoutWrapperNoMFA() {
  return (
    <ProtectedRoute requireOnboarding={false} requireMFA={false}>
      <AppLayout>
        <SuspensePage>
          <Outlet />
        </SuspensePage>
      </AppLayout>
    </ProtectedRoute>
  );
}

// Onboarding layout - no sidebar, no MFA, no onboarding requirement
function OnboardingLayoutWrapper() {
  return (
    <ProtectedRoute requireOnboarding={false} requireMFA={false}>
      <SuspensePage>
        <Outlet />
      </SuspensePage>
    </ProtectedRoute>
  );
}

// Protected advisor layout (no onboarding required, MFA optional)
function AdvisorLayoutWrapper() {
  return (
    <ProtectedRoute requireOnboarding={false}>
      <AdvisorProvider>
        <AppLayout>
          <SuspensePage>
            <Outlet />
          </SuspensePage>
        </AppLayout>
      </AdvisorProvider>
    </ProtectedRoute>
  );
}

// Protected admin layout (requires admin role, no onboarding/MFA for internal tooling)
function AdminLayoutWrapper() {
  return (
    <AdminProtectedRoute>
      <AppLayout>
        <AdminCommandPalette />
        <SuspensePage>
          <Outlet />
        </SuspensePage>
      </AppLayout>
    </AdminProtectedRoute>
  );
}

// Define all routes once, then we'll use them for both default and localized paths
// Main app routes (requires onboarding AND MFA)
const appRoutes = [
  { path: 'dashboard', element: <DashboardPage /> },
  // Legacy redirects - redirect to unified Financials hub
  { path: 'intake', element: <Navigate to="/financials?tab=profile" replace /> },
  { path: 'net-worth', element: <Navigate to="/financials?tab=networth" replace /> },
  { path: 'children', element: <ChildrenPage /> },
  { path: 'support-calculator', element: <SupportCalculatorPage /> },
  { path: 'asset-emotions', element: <FirmManagedRouteGuard><AssetEmotionsPage /></FirmManagedRouteGuard> },
  { path: 'goals', element: <GoalsPage /> },
  { path: 'strategy', element: <StrategyPage /> },
  { path: 'case-intelligence', element: <CaseIntelligencePage /> },
  { path: 'know-your-spouse', element: <FirmManagedRouteGuard><KnowYourSpousePage /></FirmManagedRouteGuard> },
  { path: 'session-debrief', element: <SessionDebriefPage /> },
  { path: 'tasks', element: <TasksPage /> },
  { path: 'documents', element: <DocumentsPage /> },
  { path: 'documents/:id', element: <DocumentDetailPage /> },
  { path: 'correspondence', element: <CorrespondencePage /> },
  { path: 'correspondence/:id', element: <CorrespondenceDetailPage /> },
  { path: 'entity-planning', element: <EntityPlanningPage /> },
  { path: 'settlement-wizard', element: <FirmManagedRouteGuard><SettlementWizardPage /></FirmManagedRouteGuard> },
  { path: 'settlement-wizard/summary', element: <FirmManagedRouteGuard><SettlementSummaryPage /></FirmManagedRouteGuard> },
  { path: 'settlement-wizard/redlines', element: <FirmManagedRouteGuard><RedlinesPage /></FirmManagedRouteGuard> },
  { path: 'sibyl', element: <FirmManagedRouteGuard><SibylPage /></FirmManagedRouteGuard> },
  { path: 'forensic-report', element: <ForensicReportPage /> },
  { path: 'discovery-inbox', element: <DiscoveryInboxPage /> },
  { path: 'redline', element: <RedlineListPage /> },
  { path: 'redline/:id', element: <RedlineWorkspacePage /> },
  { path: 'case-library', element: <CaseLibraryPage /> },
  { path: 'statement-wizard', element: <Navigate to="/case-library?tab=collect" replace /> },
  { path: 'discovery', element: <DiscoveryPage /> },
  { path: 'case-readiness', element: <CaseReadinessPage /> },
  // Disclosure Collector v1 (#1745): the collector product's single page
  { path: 'collection', element: <CollectionPage /> },
  { path: 'financials', element: <FinancialDisclosurePage /> },
  { path: 'financials/connect', element: <FinancialDisclosureConnectPage /> },
  { path: 'financials/business', element: <BusinessDisclosurePage /> },
  { path: 'financials/real-estate', element: <RealEstateDisclosurePage /> },
  { path: 'financials/vehicles', element: <VehiclesDisclosurePage /> },
  { path: 'financials/bank-accounts', element: <BankAccountsDisclosurePage /> },
  { path: 'financials/credit-cards', element: <CreditCardsDisclosurePage /> },
  { path: 'financials/investments', element: <InvestmentsDisclosurePage /> },
  { path: 'financials/retirement', element: <RetirementDisclosurePage /> },
  { path: 'financials/life-insurance', element: <LifeInsuranceDisclosurePage /> },
  { path: 'financials/health-insurance', element: <HealthInsuranceDisclosurePage /> },
  { path: 'financials/pc-insurance', element: <PCInsuranceDisclosurePage /> },
  { path: 'financials/tax-returns', element: <TaxReturnsDisclosurePage /> },
  { path: 'financials/third-party-payments', element: <ThirdPartyPaymentsDisclosurePage /> },
  { path: 'financials/crypto', element: <CryptoDisclosurePage /> },
  { path: 'financials/pay-stubs', element: <PayStubsDisclosurePage /> },
  { path: 'financials/ssa-statement', element: <SSAStatementDisclosurePage /> },
  { path: 'financials/promissory-notes', element: <PromissoryNotesDisclosurePage /> },
  { path: 'financials/leases', element: <LeasesDisclosurePage /> },
  { path: 'financials/credit-report', element: <CreditReportDisclosurePage /> },
  { path: 'financials/mortgages', element: <MortgagesDisclosurePage /> },
  { path: 'financials/contingent-liabilities', element: <ContingentLiabilitiesDisclosurePage /> },
  { path: 'export', element: <ExportPage /> },
  { path: 'notes', element: <NotesPage /> },
  { path: 'my-team', element: <MyTeamPage /> },
  { path: 'legal-team', element: <LegalTeamPage /> },
  { path: 'learn', element: <LearnPage /> },
  { path: 'learn/path-finder', element: <PathFinderPage /> },
  { path: 'learn/:category', element: <LearnCategoryPage /> },
  { path: 'learn/:category/:slug', element: <LearnArticlePage /> },
  { path: 'ask-sage', element: <AskSagePage /> },
  { path: 'upgrade', element: <UpgradePage /> },
  { path: 'help', element: <HelpPage /> },
  { path: 'help/:section/:guide', element: <GuidePage /> },
  { path: 'legal-process', element: <LegalProcessPage /> },
];

// Routes that need auth but NOT MFA (so users can set up MFA)
const appRoutesNoMFA = [
  { path: 'settings', element: <SettingsPage /> },
  // OAuth consent for the collector MCP server (M7 #1754): reachable in every preset, no onboarding/MFA gate.
  { path: 'oauth/authorize', element: <OAuthAuthorizePage /> },
];

// Onboarding route - separate because it has no sidebar
// The collector product onboards in two screens (M14 #1752); every other preset keeps the full intake.
function OnboardingSwitch() {
  const preset = useExperiencePreset();
  return preset === 'collector' ? <CollectorOnboardingPage /> : <OnboardingPage />;
}

const onboardingRoutes = [
  { path: 'onboarding', element: <OnboardingSwitch /> },
  { path: 'welcome-paid', element: <WelcomePaidPage /> },
];

const advisorRoutes = [
  { path: 'advisor', element: <AdvisorDashboardPage /> },
  { path: 'advisor/add-client', element: <AdvisorAddClientPage /> },
  { path: 'advisor/case/:clientId', element: <AdvisorCasePage /> },
  { path: 'advisor/case/:clientId/:section', element: <AdvisorCasePage /> },
  { path: 'advisor/settings/billing', element: <AdvisorBillingPage /> },
  { path: 'advisor/settings/privilege', element: <AdvisorPrivilegePage /> },
  // The DPA / compliance acknowledgment page existed but had no route (found 2026-10-01).
  { path: 'advisor/settings/compliance', element: <AdvisorCompliancePage /> },
  { path: 'advisor/team', element: <AdvisorTeamPage /> },
  { path: 'advisor/ask-sage', element: <AdvisorAskSagePage /> },
  { path: 'advisor/referrals', element: <AdvisorReferralsPage /> },
  { path: 'advisor/settings/branding', element: <AdvisorBrandingPage /> },
  { path: 'advisor/settings/features', element: <AdvisorFeaturesPage /> },
  { path: 'advisor/settings/integrations', element: <AdvisorIntegrationsPage /> },
  { path: 'advisor/settings/launch-checklist', element: <AdvisorLaunchChecklistPage /> },
];

// Admin routes (requires admin role)
const adminRoutes = [
  { path: 'admin', element: <AdminDashboardPage /> },
  { path: 'admin/users', element: <AdminUsersPage /> },
  { path: 'admin/users/:userId', element: <AdminUserDetailPage /> },
  { path: 'admin/profitability', element: <AdminProfitabilityPage /> },
  { path: 'admin/legal-process', element: <AdminLegalProcessPage /> },
  { path: 'admin/claims', element: <AdminClaimsPage /> },
  { path: 'admin/banned-emails', element: <AdminBannedEmailsPage /> },
  { path: 'admin/growth-intelligence', element: <AdminGrowthIntelligencePage /> },
  { path: 'admin/conversion-intelligence', element: <AdminConversionIntelligencePage /> },
  { path: 'admin/partners', element: <AdminPartnersPage /> },
  { path: 'admin/partners/:advisorId', element: <AdminPartnerDetailPage /> },
  { path: 'admin/prospects', element: <AdminProspectsPage /> },
  { path: 'admin/prospects/:prospectId', element: <AdminProspectDetailPage /> },
  { path: 'admin/prospects/import', element: <AdminProspectImportPage /> },
  { path: 'admin/prospects/dedup', element: <AdminDedupPage /> },
  { path: 'admin/partners/leaderboard', element: <AdminLeaderboardPage /> },
  { path: 'admin/portals', element: <AdminPortalsPage /> },
  { path: 'admin/portals/health', element: <AdminPortalHealthPage /> },
  { path: 'admin/commissions', element: <AdminCommissionsPage /> },
  { path: 'admin/analytics/cohorts', element: <AdminCohortAnalysisPage /> },
  { path: 'admin/reports', element: <AdminReportsPage /> },
  { path: 'admin/opt-outs', element: <AdminOptOutsPage /> },
  { path: 'admin/campaigns', element: <AdminCampaignsPage /> },
  { path: 'admin/campaigns/new', element: <AdminCampaignBuilderPage /> },
  { path: 'admin/campaigns/templates', element: <AdminCampaignTemplatesPage /> },
  { path: 'admin/campaigns/:campaignId', element: <AdminCampaignDetailPage /> },
  { path: 'admin/campaigns/:campaignId/edit', element: <AdminCampaignBuilderPage /> },
  { path: 'admin/demo-requests', element: <AdminDemoRequestsPage /> },
];

const authRoutes = [
  { path: 'auth', element: <SuspensePage><AuthPage /></SuspensePage> },
  { path: 'auth/callback', element: <SuspensePage><AuthCallbackPage /></SuspensePage> },
  { path: 'auth/callback/sharefile', element: <SuspensePage><ShareFileCallbackPage /></SuspensePage> },
  { path: 'auth/google-drive/callback', element: <SuspensePage><GoogleDriveCallbackPage /></SuspensePage> },
  { path: 'forgot-password', element: <SuspensePage><ForgotPasswordPage /></SuspensePage> },
  { path: 'reset-password', element: <SuspensePage><ResetPasswordPage /></SuspensePage> },
  { path: 'advisor-auth', element: <SuspensePage><AdvisorAuthPage /></SuspensePage> },
  // The invitation page must render for a signed-out attorney (2026-10-01: it sat behind the advisor
  // ProtectedRoute, so the link in the invitation email bounced to the client sign-in screen). It handles
  // its own not-authenticated / MFA / accept states.
  { path: 'advisor/accept', element: <SuspensePage><AdvisorAcceptPage /></SuspensePage> },
  // Public, like every other acceptance page (#1855). It used to sit inside advisorRoutes, behind
  // ProtectedRoute, so an invited colleague clicking the link in their email was bounced to sign-in and
  // never reached the page's own sign-up step — the exact bug already fixed for advisor/accept above.
  { path: 'advisor/team/accept', element: <SuspensePage><AdvisorTeamAcceptPage /></SuspensePage> },
  { path: 'advisor-signup', element: <SuspensePage><AdvisorSignupPage /></SuspensePage> },
  { path: 'client-accept', element: <SuspensePage><ClientAcceptPage /></SuspensePage> },
  // Public magic-link download (#1470) — unauthenticated by design.
  { path: 'share/:token', element: <SuspensePage><SharePage /></SuspensePage> },
  { path: 'email-confirmed', element: <SuspensePage><EmailConfirmedPage /></SuspensePage> },
  { path: 'beta-invite', element: <SuspensePage><BetaInvitePage /></SuspensePage> },
];

const marketingRoutes = [
  { index: true, element: isTenantDomain() ? <SuspensePage><TenantLoginPage /></SuspensePage> : <SuspensePage><HomePage /></SuspensePage> },
  // On tenant domains, all marketing routes redirect to the login page
  ...(isTenantDomain() ? [
    { path: '*', element: <Navigate to="/" replace /> },
  ] : []),
  { path: 'advisor-pricing', element: <SuspensePage><AdvisorPricingPage /></SuspensePage> },
  { path: 'affordable', element: <SuspensePage><AffordablePage /></SuspensePage> },
  { path: 'attorneys', element: <SuspensePage><AttorneysPage /></SuspensePage> },
  { path: 'blog', element: <SuspensePage><BlogPage /></SuspensePage> },
  { path: 'blog/:slug', element: <SuspensePage><BlogPostPage /></SuspensePage> },
  { path: 'business-divorce', element: <SuspensePage><BusinessDivorcePage /></SuspensePage> },
  { path: 'calculator', element: <SuspensePage><CalculatorPage /></SuspensePage> },
  { path: 'case-management', element: <SuspensePage><CaseManagementPage /></SuspensePage> },
  { path: 'cdfa', element: <SuspensePage><CdfaPage /></SuspensePage> },
  { path: 'compare', element: <SuspensePage><ComparePage /></SuspensePage> },
  { path: 'cpa', element: <SuspensePage><CpaPage /></SuspensePage> },
  { path: 'demo', element: <SuspensePage><DemoPage /></SuspensePage> },
  { path: 'book', element: <SuspensePage><DemoPage /></SuspensePage> },
  { path: 'divorce-strategy', element: <SuspensePage><DivorceStrategyPage /></SuspensePage> },
  { path: 'diy-divorce', element: <SuspensePage><DiyDivorcePage /></SuspensePage> },
  { path: 'financial-advisors', element: <SuspensePage><FinancialAdvisorsPage /></SuspensePage> },
  { path: 'for-professionals', element: <SuspensePage><ForProfessionalsPage /></SuspensePage> },
  { path: 'high-asset', element: <SuspensePage><HighAssetPage /></SuspensePage> },
  { path: 'law-firms', element: <SuspensePage><LawFirmsPage /></SuspensePage> },
  { path: 'mediators', element: <SuspensePage><MediatorsPage /></SuspensePage> },
  { path: 'negotiation', element: <SuspensePage><NegotiationPage /></SuspensePage> },
  { path: 'pricing', element: <Navigate to="/advisor-pricing" replace /> },
  { path: 'privacy', element: <SuspensePage><PrivacyPage /></SuspensePage> },
  { path: 'pro-se', element: <SuspensePage><ProSePage /></SuspensePage> },
  { path: 'terms', element: <SuspensePage><TermsPage /></SuspensePage> },
  { path: 'trust', element: <SuspensePage><TrustPage /></SuspensePage> },
  { path: 'privilege', element: <SuspensePage><PrivilegePage /></SuspensePage> },
  { path: 'videos', element: <SuspensePage><VideosPage /></SuspensePage> },
  { path: 'vs-sharefile', element: <SuspensePage><VsShareFilePage /></SuspensePage> },
  { path: 'guide/divorce-preparation-checklist', element: <SuspensePage><DivorcePreparationChecklistPage /></SuspensePage> },
  { path: 'partner/signup', element: <SuspensePage><PartnerSignupPage /></SuspensePage> },
  { path: 'partner/terms', element: <SuspensePage><PartnerTermsPage /></SuspensePage> },
  { path: 'partner/:slug', element: <SuspensePage><PartnerLandingPage /></SuspensePage> },
  { path: 'white-label', element: <SuspensePage><WhiteLabelPage /></SuspensePage> },
  { path: 'pilot', element: <SuspensePage><PilotSignupPage /></SuspensePage> },
  { path: 'unsubscribe', element: <SuspensePage><UnsubscribePage /></SuspensePage> },
  { path: 'guides', element: <SuspensePage><GuidesIndexPage /></SuspensePage> },
  { path: 'guides/advisor/:slug', element: <SuspensePage><GuidesAdvisorPage /></SuspensePage> },
  { path: 'guides/client/:slug', element: <SuspensePage><GuidesClientPage /></SuspensePage> },
];

// Build the shared children routes structure
function buildRouteChildren() {
  return [
    // App routes (protected, requires onboarding AND MFA)
    {
      element: <AppLayoutWrapper />,
      errorElement: <NestedRouteErrorBoundary />,
      children: appRoutes,
    },

    // App routes that don't require MFA (settings - so users can set up MFA)
    {
      element: <AppLayoutWrapperNoMFA />,
      errorElement: <NestedRouteErrorBoundary />,
      children: appRoutesNoMFA,
    },

    // Onboarding (protected, no sidebar, no MFA requirement)
    {
      element: <OnboardingLayoutWrapper />,
      errorElement: <NestedRouteErrorBoundary />,
      children: onboardingRoutes,
    },

    // Advisor routes (protected, no onboarding, MFA required)
    {
      element: <AdvisorLayoutWrapper />,
      errorElement: <NestedRouteErrorBoundary />,
      children: advisorRoutes,
    },

    // Admin routes (protected, requires admin role)
    {
      element: <AdminLayoutWrapper />,
      errorElement: <NestedRouteErrorBoundary />,
      children: adminRoutes,
    },

    // Auth routes (public)
    ...authRoutes,

    // Marketing routes (public)
    ...marketingRoutes,
  ];
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // ==========================================
      // Default routes (no locale prefix - English)
      // ==========================================
      {
        element: <LocaleSync />,
        children: buildRouteChildren(),
      },

      // ==========================================
      // Localized routes (with locale prefix)
      // Matches /es/*, /zh/*, etc.
      // ==========================================
      {
        path: ':locale',
        element: <LocaleSync />,
        children: buildRouteChildren(),
      },

      // ==========================================
      // Catch-all 404
      // ==========================================
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
